import { getErrorMessage } from '@/api/errors'
import { MarkdownEditor } from '@/components/MarkdownEditor'
import { ProjectMemberPicker } from '@/components/projects/ProjectMemberPicker'
import { extractValidationMessage } from '@/lib/validationErrors'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useUpdateWorkItem, useWorkItem } from '@/hooks'
import type { UpdateWorkItemRequest, WorkItem } from '@/types'
import { Check } from 'lucide-react'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'

const TITLE_MAX = 256
const DESCRIPTION_MAX = 50000
const EPIC_MAX = 256
const FEATURE_MAX = 256
const TECHNICAL_APPROACH_MAX = 50000

interface EditWorkItemDialogProps {
  projectId: string
  workItem: WorkItem
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditWorkItemDialog({ projectId, workItem, open, onOpenChange }: EditWorkItemDialogProps) {
  const updateWorkItem = useUpdateWorkItem(projectId)

  // The row that opened this dialog is a snapshot taken at click time. Saving sends every field,
  // so editing from that snapshot would overwrite anything changed since — the assignee most
  // visibly, because another user can set it from the row menu while this dialog is open.
  const { data: current, isPending: isLoadingWorkItem } = useWorkItem(projectId, open ? workItem.id : null)
  const source = current ?? workItem

  const { register, handleSubmit, watch, reset, control, formState: { errors } } = useForm<UpdateWorkItemRequest>({
    defaultValues: {
      title: workItem.title,
      description: workItem.description,
      technicalApproach: workItem.technicalApproach ?? '',
      epic: workItem.epic ?? '',
      feature: workItem.feature ?? '',
      assignedToUserId: workItem.assignedToUserId,
    },
  })

  useEffect(() => {
    if (open) {
      reset({
        title: source.title,
        description: source.description,
        technicalApproach: source.technicalApproach ?? '',
        epic: source.epic ?? '',
        feature: source.feature ?? '',
        assignedToUserId: source.assignedToUserId,
      })
    }
  }, [open, source, reset])

  const titleValue = watch('title') ?? ''
  const descriptionValue = watch('description') ?? ''
  const epicValue = watch('epic') ?? ''
  const featureValue = watch('feature') ?? ''
  const technicalApproachValue = watch('technicalApproach') ?? ''

  const onSubmit = async (data: UpdateWorkItemRequest) => {
    try {
      const title = data.title.trim()
      const description = data.description.trim()
      const technicalApproach = data.technicalApproach?.trim()
      const epic = data.epic?.trim()
      const feature = data.feature?.trim()
      await updateWorkItem.mutateAsync({
        id: workItem.id,
        data: {
          title,
          description,
          assignedToUserId: data.assignedToUserId ?? null,
          ...(technicalApproach ? { technicalApproach } : {}),
          ...(epic ? { epic } : {}),
          ...(feature ? { feature } : {}),
        },
      })
      toast.success('Work item updated successfully')
      onOpenChange(false)
    } catch (error) {
      toast.error(
        extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to update work item',
      )
    }
  }

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      reset({
        title: source.title,
        description: source.description,
        technicalApproach: source.technicalApproach ?? '',
        epic: source.epic ?? '',
        feature: source.feature ?? '',
        assignedToUserId: source.assignedToUserId,
      })
    }
    onOpenChange(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] flex flex-col p-0 rounded-none border-none gap-0">
        <DialogHeader className="px-8 py-4 border-b shrink-0 bg-background">
          <DialogTitle className="text-xl font-bold">Edit Work Item</DialogTitle>
        </DialogHeader>
        {isLoadingWorkItem ? (
          <div
            role="status"
            aria-live="polite"
            className="flex flex-1 items-center justify-center text-sm text-muted-foreground"
          >
            Loading work item...
          </div>
        ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto bg-input-background">
            <div className="max-w-4xl mx-auto py-8 px-6 space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-title">Title <span className="text-red-500">*</span></Label>
                  <span className="text-xs text-muted-foreground">{titleValue.trim().length}/{TITLE_MAX}</span>
                </div>
                <Input
                  id="edit-title"
                  placeholder="Enter work item title"
                  {...register('title', {
                    required: 'Work item title is required',
                    validate: (value) => {
                      const trimmed = value.trim()
                      if (trimmed.length === 0) return 'Work item title is required'
                      if (trimmed.length > TITLE_MAX) return `Title must be at most ${TITLE_MAX} characters`
                      return true
                    },
                  })}
                />
                {errors.title && (
                  <p role="alert" className="text-sm text-destructive">{errors.title.message}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="edit-epic">Epic</Label>
                    <span className="text-xs text-muted-foreground">{epicValue.trim().length}/{EPIC_MAX}</span>
                  </div>
                  <Input
                    id="edit-epic"
                    placeholder="Enter epic name"
                    {...register('epic', {
                      validate: (value) => {
                        if (value && value.trim().length > EPIC_MAX) return `Epic must be at most ${EPIC_MAX} characters`
                        return true
                      },
                    })}
                  />
                  {errors.epic && (
                    <p role="alert" className="text-sm text-destructive">{errors.epic.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="edit-feature">Feature</Label>
                    <span className="text-xs text-muted-foreground">{featureValue.trim().length}/{FEATURE_MAX}</span>
                  </div>
                  <Input
                    id="edit-feature"
                    placeholder="Enter feature name"
                    {...register('feature', {
                      validate: (value) => {
                        if (value && value.trim().length > FEATURE_MAX) return `Feature must be at most ${FEATURE_MAX} characters`
                        return true
                      },
                    })}
                  />
                  {errors.feature && (
                    <p role="alert" className="text-sm text-destructive">{errors.feature.message}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-assigned-to">Assign to</Label>
                  <Controller
                    name="assignedToUserId"
                    control={control}
                    render={({ field }) => (
                      <ProjectMemberPicker
                        projectId={projectId}
                        inputId="edit-assigned-to"
                        value={field.value ?? null}
                        onChange={field.onChange}
                        valueLabel={source.assignedToDisplayName || source.assignedToEmail}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Description <span className="text-red-500">*</span></Label>
                  <span className="text-xs text-muted-foreground">{descriptionValue.trim().length}/{DESCRIPTION_MAX}</span>
                </div>
                <div className="h-[calc(100vh-360px)] flex flex-col">
                  <Controller
                    name="description"
                    control={control}
                    rules={{
                      required: 'Work item description is required',
                      validate: (value) => {
                        const trimmed = value.trim()
                        if (trimmed.length === 0) return 'Work item description is required'
                        if (trimmed.length > DESCRIPTION_MAX) return `Description must be at most ${DESCRIPTION_MAX} characters`
                        return true
                      },
                    }}
                    render={({ field }) => (
                      <MarkdownEditor
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Enter work item description"
                        fillHeight
                        tables
                      />
                    )}
                  />
                </div>
                {errors.description && (
                  <p role="alert" className="text-sm text-destructive">{errors.description.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-technicalApproach">Technical Approach</Label>
                  <span className="text-xs text-muted-foreground">{technicalApproachValue.trim().length}/{TECHNICAL_APPROACH_MAX}</span>
                </div>
                <div className="h-[calc(100vh-360px)] flex flex-col">
                  <Controller
                    name="technicalApproach"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (value && value.trim().length > TECHNICAL_APPROACH_MAX) return `Technical Approach must be at most ${TECHNICAL_APPROACH_MAX} characters`
                        return true
                      },
                    }}
                    render={({ field }) => (
                      <MarkdownEditor
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        placeholder="Enter technical approach"
                        fillHeight
                        tables
                      />
                    )}
                  />
                </div>
                {errors.technicalApproach && (
                  <p role="alert" className="text-sm text-destructive">{errors.technicalApproach.message}</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="px-8 py-4 border-t shrink-0 bg-background">
            <Button type="button" variant="ghost" onClick={() => handleClose(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateWorkItem.isPending}
            >
              <Check className="h-5 w-5" />
              {updateWorkItem.isPending ? 'Saving...' : 'Save Work Item'}
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
